import { invokeFunction } from '@/lib/utils/invokeFunction';

/**
 * Type definition for the premium expiration response
 */
interface PremiumExpirationResponse {
  success: boolean;
  expired_count: number;
  message?: string;
  error?: string;
  details?: string;
}

/**
 * Manually trigger the expiration check for premium listings
 * This is primarily used by administrators to run the check on demand
 */
export const runPremiumExpirationCheck = async (): Promise<{
  success: boolean;
  expired_count?: number;
  error?: string;
  details?: string;
}> => {
  try {
    const { data, error } = await invokeFunction<PremiumExpirationResponse>('run-premium-expiration');
    
    if (error) {
      return { 
        success: false, 
        error: error.message || 'Unknown error occurred',
        details: error.toString()
      };
    }
    
    if (!data) {
      return { success: false, error: 'No data returned from function' };
    }

    if (data.error) {
      return { 
        success: false, 
        error: data.error, 
        details: data.details 
      };
    }
    
    return { 
      success: true, 
      expired_count: data.expired_count || 0 
    };
  } catch (error: any) {
    return { 
      success: false, 
      error: error.message || 'Failed to run premium expiration check',
      details: error.toString() 
    };
  }
};

/**
 * Type definition for the reset user credits response
 */
interface ResetUserCreditsResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Manually trigger a credit reset for a specific user.
 * This is primarily used by administrators.
 * @param userIdToReset The ID of the user whose credits should be reset.
 */
export const resetUserCredits = async (userIdToReset: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const { data, error } = await invokeFunction<ResetUserCreditsResponse>('reset-user-credits', { userIdToReset });
    
    if (error) {
      return { 
        success: false, 
        error: error.message || 'Unknown error occurred while invoking reset-user-credits',
      };
    }
    
    if (!data) {
      return { success: false, error: 'No data returned from reset-user-credits function' };
    }

    if (!data.success && data.error) {
      return { 
        success: false, 
        error: data.error, 
      };
    }
    
    return { 
      success: data.success, 
      message: data.message
    };
  } catch (error: any) {
    return { 
      success: false, 
      error: error.message || 'Failed to run reset user credits function',
    };
  }
};
